import React, { useState } from "react";
import { X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useVisitor } from "../context/VisitorAuthContext";

const formatErr = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return "Something went wrong. Please try again.";
};

export const AuthModal = () => {
  const { authOpen, closeAuth, loginEmail, registerEmail, loginGoogle } = useVisitor();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  if (!authOpen) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await loginEmail(form.email, form.password);
        toast.success("Welcome back to the vault.");
      } else {
        await registerEmail(form.email, form.password, form.name);
        toast.success("Your reading room is ready.");
      }
      closeAuth();
      setForm({ email: "", password: "", name: "" });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full font-ui text-sm px-3 py-2.5 bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm outline-none focus:border-[#9E4535] focus:ring-2 focus:ring-[#F5E8EA]";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#1A1615]/60 backdrop-blur-sm cv-fade-up"
      data-testid="auth-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) closeAuth(); }}
    >
      <div className="relative w-full max-w-md bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm shadow-2xl p-7 sm:p-9">
        <button
          onClick={closeAuth}
          data-testid="auth-modal-close"
          className="absolute top-4 right-4 text-[#7A6C5D] hover:text-[#9E4535] transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#C5A059]" />
          <p className="caption-label">The Reading Room</p>
        </div>
        <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-[#1A1615] mb-1">
          {mode === "login" ? "Welcome back" : "Join the vault"}
        </h2>
        <p className="font-body text-sm text-[#4A4036] mb-6">
          Sign in to save your favourite curios and find them again anytime.
        </p>

        <button
          type="button"
          onClick={loginGoogle}
          data-testid="google-signin-button"
          className="w-full flex items-center justify-center gap-3 font-ui text-sm font-semibold px-4 py-3 bg-white border border-[#D8CEB9] rounded-sm text-[#1A1615] hover:border-[#9E4535] transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-[#D8CEB9]" />
          <span className="font-mono-cv text-[10px] text-[#B8AC9C] uppercase">or</span>
          <div className="flex-1 h-px bg-[#D8CEB9]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="caption-label block mb-1.5">Name</label>
              <input data-testid="auth-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
            </div>
          )}
          <div>
            <label className="caption-label block mb-1.5">Email</label>
            <input data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
          </div>
          <div>
            <label className="caption-label block mb-1.5">Password</label>
            <input data-testid="auth-password-input" type="password" required value={form.password} onChange={set("password")} placeholder={mode === "register" ? "At least 6 characters" : "Your password"} className={inputCls} />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-button"
            className="w-full font-ui text-sm font-semibold px-6 py-3 bg-[#9E4535] text-[#FAF7F0] rounded-sm hover:bg-[#82382B] transition-colors disabled:opacity-60"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="font-body text-sm text-[#4A4036] mt-5 text-center">
          {mode === "login" ? "New to CurioVault?" : "Already have an account?"}{" "}
          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => setMode((m) => (m === "login" ? "register" : "login"))}
            className="text-[#9E4535] font-semibold hover:underline"
          >
            {mode === "login" ? "Create one" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
};
